// ╔══════════════════════════════════════════════════════════════════╗
// ║                    ADMIN APPLICATION                             ║
// ╚══════════════════════════════════════════════════════════════════╝

import React, { useState, useEffect, createContext, useContext } from 'react'
import { AdminAuthAPI } from '../services/adminApi'
import AdminLogin from './pages/AdminLogin'
import AdminDashboard from './AdminDashboard'
import GoogleTranslate from '../components/GoogleTranslate'

// Admin Context
const AdminContext = createContext(null)

export const useAdmin = () => useContext(AdminContext)

export default function AdminApp() {
  const [admin, setAdmin] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    checkAuth()
  }, [])

  const checkAuth = async () => {
    if (AdminAuthAPI.isAuthenticated()) {
      const storedAdmin = AdminAuthAPI.getStoredAdmin()
      if (storedAdmin) {
        setAdmin(storedAdmin)
        // Verify token is still valid
        const res = await AdminAuthAPI.getCurrentAdmin()
        if (res.success) {
          setAdmin(res.data.admin)
          localStorage.setItem('adminUser', JSON.stringify(res.data.admin))
        } else {
          handleLogout()
        }
      }
    }
    setLoading(false)
  }

  const handleLogin = (adminData) => {
    setAdmin(adminData)
  }

  const handleLogout = async () => {
    await AdminAuthAPI.logout()
    setAdmin(null)
  }

  const hasPermission = (permission) => {
    if (!admin) return false
    if (admin.role?.name === 'super_admin') return true
    return admin.permissions?.includes(permission)
  }

  const hasAnyPermission = (...permissions) => {
    return permissions.some(p => hasPermission(p))
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading Admin Panel...</p>
        </div>
      </div>
    )
  }

  if (!admin) {
    return <AdminLogin onLogin={handleLogin} />
  }

  return (
    <AdminContext.Provider value={{ admin, hasPermission, hasAnyPermission, logout: handleLogout }}>
      <AdminDashboard onLogout={handleLogout} />
      {/* Global Language Selector for Admin */}
      <GoogleTranslate variant="floating" />
    </AdminContext.Provider>
  )
}
